import {
  FiatACHPaymentForm,
  FiatCOPBeneficiaryIndividualPaymentForm,
  FiatCOPThirdPartyIndividualPaymentForm,
  FiatCvuPaymentForm,
  FiatPaymentForm,
  FiatPixPaymentForm,
  FiatSepaPaymentForm,
  FiatSpeiPaymentForm,
  FiatSwiftPaymentForm,
  FiatWirePaymentForm,
} from '@/schemas/paymentMethod';
import { BankAccountType } from '@/types/bankAccountType';
import {
  ContactAddress,
  ContactForm,
  CreateContactRequest,
  CryptoAssetType,
  CryptoChain,
  CryptoPaymentForm,
} from '@/types/contacts';
import { CopDocumentType } from '@/types/copDocumentType';
import { CountryType } from '@/types/country';
import { CurrencyCodeType } from '@/types/currency';
import { FiatAccountType } from '@/types/fiatAccountType';
import { PaymentMethodAccountType } from '@/types/paymentMethodAccountType';

export const emptyFiatPIXAccount: FiatPixPaymentForm = {
  type: PaymentMethodAccountType.fiat,
  country: CountryType.BR,
  currency: CurrencyCodeType.BRL,
  paymentMethod: FiatAccountType.pix,
  pixKey: '',
  documentNumber: '',
};

export const emptyFiatSpeiAccount: FiatSpeiPaymentForm = {
  type: PaymentMethodAccountType.fiat,
  country: CountryType.MX,
  currency: CurrencyCodeType.MXN,
  paymentMethod: FiatAccountType.spei,
  bankCode: '',
  clabe: '',
  receiverName: '',
};

export const emptyFiatACHAccount: FiatACHPaymentForm = {
  type: PaymentMethodAccountType.fiat,
  country: CountryType.US,
  currency: CurrencyCodeType.USD,
  paymentMethod: FiatAccountType.ach,
  bankName: '',
  address: ContactAddress.empty(),
  routingNumber: '',
  accountNumber: '',
};

export const emptyFiatWireAccount: FiatWirePaymentForm = {
  type: PaymentMethodAccountType.fiat,
  country: CountryType.US,
  currency: CurrencyCodeType.USD,
  paymentMethod: FiatAccountType.wire,
  bankName: '',
  address: ContactAddress.empty(),
  routingNumber: '',
  accountNumber: '',
};

export const createEmptySwiftAccount = (
  country: CountryType,
  currency: CurrencyCodeType = CurrencyCodeType.USD,
): FiatSwiftPaymentForm => ({
  type: PaymentMethodAccountType.fiat,
  paymentMethod: FiatAccountType.swift,
  country: country,
  currency: currency,
  address: ContactAddress.empty(),
  bankName: '',
  swiftCode: '',
  iban: '',
  isPobo: false,
  // Mobile number is only collected on the local-currency rail for CN/HK
  // (CN+CNY, HK+HKD); USD SWIFT to either country doesn't carry it.
  ...((country === CountryType.CN && currency === CurrencyCodeType.CNY) ||
  (country === CountryType.HK && currency === CurrencyCodeType.HKD)
    ? { mobileNumber: '' }
    : {}),
  // CNAPS is the PBOC's CNY-domestic routing code; it has no role in USD SWIFT
  // instructions to China, so only seed `bankCode` for the CNY rail.
  ...(country === CountryType.CN && currency === CurrencyCodeType.CNY ? { bankCode: '' } : {}),
});

export const emptyThirdPartyFiatCOPAccount: FiatCOPThirdPartyIndividualPaymentForm = {
  firstName: '',
  lastName: '',
  recipientType: 'individual',
  type: PaymentMethodAccountType.fiat,
  country: CountryType.CO,
  currency: CurrencyCodeType.COP,
  paymentMethod: FiatAccountType.cop,
  bankType: BankAccountType.checking,
  bankAccount: '',
  bankCode: '',
  thirdPartyWithdrawal: true,
  documentId: '',
  documentType: CopDocumentType.UNSET,
  email: '',
};

export const emptyBeneficiaryFiatCOPAccount: FiatCOPBeneficiaryIndividualPaymentForm = {
  firstName: '',
  lastName: '',
  recipientType: 'individual',
  type: PaymentMethodAccountType.fiat,
  name: '',
  country: CountryType.CO,
  currency: CurrencyCodeType.COP,
  paymentMethod: FiatAccountType.cop,
  bankType: BankAccountType.checking,
  bankAccount: '',
  bankCode: '',
};

export const createEmptySepaAccount = (country: CountryType): FiatSepaPaymentForm => ({
  type: PaymentMethodAccountType.fiat,
  paymentMethod: FiatAccountType.sepa,
  country: country,
  currency: CurrencyCodeType.EUR,
  iban: '',
});

export const emptyFiatCVUAccount: FiatCvuPaymentForm = {
  type: PaymentMethodAccountType.fiat,
  country: CountryType.AR,
  currency: CurrencyCodeType.ARS,
  paymentMethod: FiatAccountType.cvu,
  cvu: '',
  recipientName: '',
};

export const emptyCryptoAccount: CryptoPaymentForm = {
  type: PaymentMethodAccountType.crypto,
  name: '',

  country: CountryType.US,
  currency: CurrencyCodeType.USD,
  assetType: CryptoAssetType.unset,
  chain: CryptoChain.unset,
  walletAddress: '',
  destination: '',
};

export const contactFormToContactDTO = (
  beneficiaryId: string,
  contact: ContactForm,
): CreateContactRequest => {
  const { address, country, ...entity } = contact;

  return {
    beneficiaryId: beneficiaryId,
    address: address,
    country: country,
    entity: entity,
    belongsToBeneficiary: false,
  };
};

export const emptyByFiatAccountType = (
  accountType: FiatAccountType,
  country: CountryType,
  contactId: string | null,
  currency: CurrencyCodeType = CurrencyCodeType.USD,
): FiatPaymentForm => {
  switch (accountType) {
    case FiatAccountType.ach:
      return emptyFiatACHAccount;
    case FiatAccountType.swift:
      return createEmptySwiftAccount(country, currency);
    case FiatAccountType.sepa:
      return createEmptySepaAccount(country);
    case FiatAccountType.wire:
      return emptyFiatWireAccount;
    case FiatAccountType.pix:
      return emptyFiatPIXAccount;
    case FiatAccountType.spei:
      return emptyFiatSpeiAccount;
    case FiatAccountType.cop:
      return contactId ? emptyThirdPartyFiatCOPAccount : emptyBeneficiaryFiatCOPAccount;
    case FiatAccountType.cvu:
      return emptyFiatCVUAccount;
    case FiatAccountType.crypto:
    case FiatAccountType.unset:
      throw new Error('Invalid account type');
  }
};
